import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

const ServicesSection = () => {
  const { t } = useTranslation(['common', 'services']);
  const [selectedService, setSelectedService] = useState<number | null>(null);
  const [showCanadaNotification, setShowCanadaNotification] = useState(false);

  const services = [
    {
      icon: 'ri-plane-line',
      title: t('services:airportTransfers'),
      description: t('services:airportTransfersDesc'),
      features: t('services:airportTransfersFeatures', { returnObjects: true }) as string[],
      image: 'https://readdy.ai/api/search-image?query=Professional%20black%20Mercedes%20van%20for%20airport%20transfer%20service%2C%20luxury%20commercial%20vehicle%20with%20tinted%20windows%2C%20premium%20transportation%20business%2C%20modern%20airport%20setting%2C%20professional%20automotive%20photography%2C%20sleek%20design&width=400&height=300&seq=mercedes-van-transfer&orientation=landscape',
      detailedInfo: {
        description: t('services:airportTransfersDetailDesc'),
        included: t('services:airportTransfersIncluded', { returnObjects: true }) as string[],
        list: t('services:airports', { returnObjects: true }) as string[]
      }
    },
    {
      icon: 'ri-map-line',
      title: t('services:touristCircuits'),
      description: t('services:touristCircuitsDesc'),
      features: t('services:touristCircuitsFeatures', { returnObjects: true }) as string[],
      image: 'https://readdy.ai/api/search-image?query=Moroccan%20desert%20tour%20with%20luxury%204x4%20vehicle%2C%20tourists%20enjoying%20scenic%20landscape%20of%20Sahara%20dunes%2C%20traditional%20Berber%20camp%20in%20background%2C%20golden%20sunset%20lighting%2C%20adventure%20tourism%2C%20premium%20travel%20experience&width=400&height=300&seq=desert-tour&orientation=landscape',
      detailedInfo: {
        description: t('services:touristCircuitsDetailDesc'),
        included: t('services:touristCircuitsIncluded', { returnObjects: true }) as string[],
        list: t('services:destinations', { returnObjects: true }) as string[]
      }
    },
    {
      icon: 'ri-vip-crown-line',
      title: t('services:crewTransport'),
      description: t('services:crewTransportDesc'),
      features: t('services:crewTransportFeatures', { returnObjects: true }) as string[],
      image: 'https://readdy.ai/api/search-image?query=Luxury%20VIP%20transport%20service%20with%20premium%20black%20sedan%2C%20elegant%20interior%20with%20leather%20seats%2C%20professional%20business%20travel%2C%20sophisticated%20urban%20setting%2C%20modern%20city%20background%2C%20high-end%20automotive%20photography&width=400&height=300&seq=vip-transport&orientation=landscape',
      detailedInfo: {
        description: t('services:crewTransportDetailDesc'),
        included: t('services:crewTransportIncluded', { returnObjects: true }) as string[],
        list: t('services:occasions', { returnObjects: true }) as string[]
      }
    },
    {
      icon: 'ri-time-line',
      title: t('services:dayExcursions'),
      description: t('services:dayExcursionsDesc'),
      features: t('services:dayExcursionsFeatures', { returnObjects: true }) as string[],
      image: 'https://readdy.ai/api/search-image?query=Day%20trip%20excursion%20to%20Moroccan%20historical%20sites%2C%20tourists%20visiting%20ancient%20medina%20and%20traditional%20architecture%2C%20comfortable%20tour%20vehicle%2C%20cultural%20discovery%20experience%2C%20warm%20Mediterranean%20lighting&width=400&height=300&seq=day-excursion&orientation=landscape',
      detailedInfo: {
        description: t('services:dayExcursionsDetailDesc'),
        included: t('services:dayExcursionsIncluded', { returnObjects: true }) as string[],
        list: t('services:dayDestinations', { returnObjects: true }) as string[]
      }
    },
    {
      icon: 'ri-car-line',
      title: t('services:chauffeurService'),
      description: t('services:chauffeurServiceDesc'),
      features: t('services:chauffeurServiceFeatures', { returnObjects: true }) as string[],
      image: 'https://readdy.ai/api/search-image?query=Professional%20black%20Mercedes%20van%20for%20chauffeur%20service%2C%20luxury%20commercial%20vehicle%20with%20tinted%20windows%2C%20premium%20transportation%20business%2C%20modern%20urban%20setting%2C%20professional%20automotive%20photography%2C%20sleek%20design%20without%20any%20people%20visible&width=400&height=300&seq=mercedes-van-chauffeur&orientation=landscape',
      detailedInfo: {
        description: t('services:chauffeurServiceDetailDesc'),
        included: t('services:chauffeurServiceIncluded', { returnObjects: true }) as string[],
        list: t('services:vehicles', { returnObjects: true }) as string[]
      }
    },
    {
      icon: 'ri-maple-leaf-line',
      title: t('services:canadaServices'),
      description: t('services:canadaServicesDesc'),
      features: t('services:canadaServicesFeatures', { returnObjects: true }) as string[],
      image: 'https://readdy.ai/api/search-image?query=Canadian%20winter%20transport%20service%20with%20luxury%20SUV%20in%20snowy%20Montreal%20cityscape%2C%20professional%20winter%20travel%2C%20premium%20vehicle%20equipped%20for%20cold%20weather%2C%20urban%20Canadian%20setting%20with%20modern%20architecture&width=400&height=300&seq=canada-service&orientation=landscape',
      detailedInfo: {
        description: t('services:canadaServicesDetailDesc'),
        included: t('services:canadaServicesIncluded', { returnObjects: true }) as string[],
        list: t('services:cities', { returnObjects: true }) as string[]
      }
    }
  ];

  const openModal = (index: number) => {
    // Si c'est le service Canada (index 5), afficher la notification
    if (index === 5) {
      setShowCanadaNotification(true);
      setTimeout(() => {
        setShowCanadaNotification(false);
      }, 3000);
      return;
    }
    
    setSelectedService(index);
    document.body.style.overflow = 'hidden';
  };

  const closeModal = () => {
    setSelectedService(null);
    document.body.style.overflow = 'unset';
  };

  return (
    <>
      <section id="services" className="py-12 sm:py-16 md:py-20 bg-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          {/* Notification pour Services Canada */}
          {showCanadaNotification && (
            <div className="fixed top-4 left-1/2 transform -translate-x-1/2 bg-blue-600 text-white px-6 py-3 rounded-lg shadow-lg z-50 flex items-center gap-2">
              <i className="ri-information-line text-xl"></i>
              <span className="font-medium">Services Canada - Bientôt disponible</span>
            </div>
          )}

          <div className="text-center mb-10 sm:mb-12 md:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-charcoal-800 mb-4 sm:mb-6">
              {t('servicesTitle')}
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-charcoal-600 max-w-3xl mx-auto leading-relaxed px-4">
              {t('servicesDescription')}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {services.map((service, index) => (
              <div 
                key={index}
                className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2"
              >
                <div className="relative h-40 sm:h-48 overflow-hidden">
                  <img 
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute top-3 left-3 sm:top-4 sm:left-4 w-10 h-10 sm:w-12 sm:h-12 bg-blue-600 rounded-full flex items-center justify-center">
                    <i className={`${service.icon} text-white text-lg sm:text-xl`}></i>
                  </div>
                  {/* Badge "Bientôt" pour Services Canada */}
                  {index === 5 && (
                    <div className="absolute top-3 right-3 sm:top-4 sm:right-4 bg-yellow-500 text-white px-3 py-1 rounded-full text-xs font-semibold">
                      Bientôt
                    </div>
                  )}
                </div>
                
                <div className="p-4 sm:p-6">
                  <h3 className="text-lg sm:text-xl font-semibold text-charcoal-800 mb-2 sm:mb-3">
                    {service.title}
                  </h3>
                  <p className="text-sm sm:text-base text-charcoal-600 mb-3 sm:mb-4 leading-relaxed">
                    {service.description}
                  </p>
                  
                  <ul className="space-y-1.5 sm:space-y-2">
                    {service.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center text-xs sm:text-sm text-charcoal-600">
                        <i className="ri-check-line text-blue-600 mr-2 flex-shrink-0"></i>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <button 
                    onClick={() => openModal(index)}
                    className="mt-4 sm:mt-6 w-full bg-blue-600 text-white py-2.5 sm:py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium whitespace-nowrap cursor-pointer text-sm sm:text-base"
                  >
                    {t('learnMore')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Modal */}
      {selectedService !== null && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto my-4">
            <div className="relative">
              {/* Header avec image */}
              <div className="relative h-48 sm:h-56 md:h-64 overflow-hidden rounded-t-2xl">
                <img 
                  src={services[selectedService].image}
                  alt={services[selectedService].title}
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                <button 
                  onClick={closeModal}
                  className="absolute top-3 right-3 sm:top-4 sm:right-4 w-8 h-8 sm:w-10 sm:h-10 bg-white rounded-full flex items-center justify-center hover:bg-gray-100 transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-gray-600 text-lg sm:text-xl"></i>
                </button>
                <div className="absolute bottom-3 left-4 sm:bottom-4 sm:left-6">
                  <div className="w-12 h-12 sm:w-16 sm:h-16 bg-blue-600 rounded-full flex items-center justify-center mb-3 sm:mb-4">
                    <i className={`${services[selectedService].icon} text-white text-xl sm:text-2xl`}></i>
                  </div>
                  <h2 className="text-2xl sm:text-3xl font-bold text-white">{services[selectedService].title}</h2>
                </div>
              </div>

              {/* Contenu */}
              <div className="p-4 sm:p-6 md:p-8">
                <p className="text-base sm:text-lg text-charcoal-600 mb-6 sm:mb-8 leading-relaxed">
                  {services[selectedService].detailedInfo.description}
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
                  {/* Inclus dans le service */}
                  <div>
                    <h3 className="text-lg sm:text-xl font-semibold text-charcoal-800 mb-3 sm:mb-4 flex items-center">
                      <i className="ri-check-double-line text-blue-600 mr-2"></i>
                      {t('includedInService')}
                    </h3>
                    <ul className="space-y-2 sm:space-y-3">
                      {services[selectedService].detailedInfo.included.map((item, index) => (
                        <li key={index} className="flex items-start">
                          <i className="ri-check-line text-blue-600 mr-2 sm:mr-3 mt-1 flex-shrink-0"></i>
                          <span className="text-sm sm:text-base text-charcoal-600">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Informations pratiques */}
                  <div className="space-y-4 sm:space-y-6">
                    <div>
                      <h4 className="font-semibold text-charcoal-800 mb-2 flex items-center text-sm sm:text-base">
                        <i className="ri-map-pin-line text-blue-600 mr-2"></i>
                        {selectedService === 0 ? 'Aéroports' : 
                         selectedService === 1 ? 'Destinations' :
                         selectedService === 2 ? 'Occasions' :
                         selectedService === 3 ? 'Destinations' :
                         selectedService === 4 ? 'Véhicules' :
                         'Villes'}
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {services[selectedService].detailedInfo.list.map((item, index) => (
                          <span key={index} className="bg-blue-100 text-blue-800 px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm">
                            {item}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Boutons d'action */}
                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mt-6 sm:mt-8 pt-4 sm:pt-6 border-t border-gray-200">
                  <button 
                    onClick={() => {
                      const element = document.getElementById('reservation');
                      if (element) {
                        element.scrollIntoView({ behavior: 'smooth' });
                        closeModal();
                      }
                    }}
                    className="flex-1 bg-blue-600 text-white py-2.5 sm:py-3 px-4 sm:px-6 rounded-lg hover:bg-blue-700 transition-colors font-medium whitespace-nowrap cursor-pointer text-sm sm:text-base"
                  >
                    <i className="ri-calendar-line mr-2"></i>
                    {t('reserveThisService')}
                  </button>
                  <a 
                    href="https://wa.me/212666204299" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex-1 bg-green-600 text-white py-2.5 sm:py-3 px-4 sm:px-6 rounded-lg hover:bg-green-700 transition-colors font-medium text-center whitespace-nowrap cursor-pointer text-sm sm:text-base"
                  >
                    <i className="ri-whatsapp-line mr-2"></i>
                    {t('chatOnWhatsApp')}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ServicesSection;
